/**
 * Bundle 80 Task 1 - Admin Success Stories area.
 * URL: /admin/success-stories
 *
 * Bundle 80.1 polish:
 *   - Side-by-side draft / preview panel with attribution + photo permission
 *     rendered as the reader would see it.
 *   - Server auto-picks the more-private attribution across both parties on
 *     publish. UI shows the resulting preview so the admin can confirm.
 *   - Revocation requires a >=8 char reason.
 *   - Photo permission checkboxes per participant default OFF.
 *   - Publish button disabled until every identifiable participant has approved.
 * No em/en dashes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

const AdminSuccessStoriesPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/success-stories');
      setRows(data?.candidates || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const contact = async (id) => {
    try {
      await api.post(`/admin/success-stories/${id}/contact-participant`, {});
      toast.success('Marked as contacting participant.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const publish = async (id) => {
    try {
      await api.post(`/admin/success-stories/${id}/publish`, {});
      toast.success('Published.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-success-stories-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800">Bundle 80.1 * Phase 5 polish</div>
          <h1 className="font-display text-3xl font-bold text-primary-900 mt-1">Success stories</h1>
          <p className="text-neutral-600 text-sm mt-1">Only stories that both people have approved word-for-word can be published.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="success-stories-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="space-y-3">
        {rows.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-neutral-500 text-sm">No candidates yet.</CardContent></Card>
        ) : null}
        {rows.map((r) => (
          <Card key={r.id} data-testid={`success-row-${r.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="capitalize">{(r.status || 'candidate').replace(/_/g, ' ')}</Badge>
                {r.both_reported_positive ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Both positive</Badge>
                ) : null}
                <Badge variant="outline">Permission: {r.permission_to_contact_status || 'not_requested'}</Badge>
                <Badge variant="outline">Source: {r.connection_source || 'facilitated'}</Badge>
                {r.sixty_day_status ? (
                  <Badge variant="outline">60d: {String(r.sixty_day_status).replace(/_/g, ' ')}</Badge>
                ) : null}
                <span className="text-xs text-neutral-500 font-mono ml-auto">{r.event_id}</span>
              </div>
              <div className="text-sm text-primary-900">
                Participants: {(r.participants || []).join(', ')}
              </div>
              {r.publication?.published_at ? (
                <div className="text-xs text-emerald-700" data-testid={`success-published-${r.id}`}>
                  Published {new Date(r.publication.published_at).toLocaleDateString()}
                </div>
              ) : null}
              {r.publication?.revocation_snapshot ? (
                <div className="text-xs text-rose-700" data-testid={`success-revoked-${r.id}`}>
                  Revoked {new Date(r.publication.revocation_snapshot.revoked_at).toLocaleDateString()}
                  {r.publication.revocation_snapshot.reason ? ` - ${r.publication.revocation_snapshot.reason}` : ''}
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={r.permission_to_contact_status !== 'granted' || (r.status === 'contacting_participant_a')}
                  onClick={() => contact(r.id)} data-testid={`success-contact-btn-${r.id}`}>
                  Contact participant to develop
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`success-edit-btn-${r.id}`}>
                  Draft + approve
                </Button>
                <Button
                  size="sm"
                  onClick={() => publish(r.id)}
                  disabled={!isPublishReady(r)}
                  className="bg-primary-900 text-white hover:bg-primary-800"
                  data-testid={`success-publish-btn-${r.id}`}
                >
                  Publish
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRevoking(r)}
                  data-testid={`success-revoke-btn-${r.id}`}>Revoke</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {editing ? <PublicationEditor candidate={editing} onClose={() => { setEditing(null); load(); }} /> : null}
      {revoking ? <RevokeDialog candidate={revoking} onClose={() => { setRevoking(null); load(); }} /> : null}
    </div>
  );
};

function isPublishReady(candidate) {
  const pub = candidate.publication || {};
  if (!pub.final_story_text) return false;
  const approvals = pub.participant_approvals || {};
  const identifiable = (candidate.participants || []).filter((p) => p && !String(p).startsWith('guest:'));
  if (!identifiable.length) return false;
  return identifiable.every((p) => !!approvals[p]);
}

const ATTRIBUTION_ORDER = { anonymous: 0, initials: 1, first_name: 2 };
function pickMostPrivate(attr, participants) {
  const picks = participants.map((p) => attr[p]).filter(Boolean);
  if (!picks.length) return 'initials';
  return picks.reduce((acc, p) => (ATTRIBUTION_ORDER[p] < ATTRIBUTION_ORDER[acc] ? p : acc), picks[0]);
}

const PublicationEditor = ({ candidate, onClose }) => {
  const pub = candidate.publication || {};
  const identifiable = (candidate.participants || []).filter((p) => p && !String(p).startsWith('guest:'));
  const [text, setText] = useState(pub.final_story_text || '');
  const [attr, setAttr] = useState(pub.attribution_per_participant || {});
  const [approvals, setApprovals] = useState(pub.participant_approvals || {});
  const [photoPerm, setPhotoPerm] = useState(pub.photo_permission_per_participant || {});
  const [saving, setSaving] = useState(false);

  const effectiveAttr = useMemo(() => pickMostPrivate(attr, identifiable), [attr, identifiable]);
  const publishReady = identifiable.length && identifiable.every((p) => !!approvals[p]) && !!text.trim();

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/success-stories/${candidate.id}/publication`, {
        final_story_text: text,
        attribution_per_participant: attr,
        participant_approvals: approvals,
        photo_permission_per_participant: photoPerm,
      });
      toast.success('Saved.');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const readerName = (participantId) => {
    if (effectiveAttr === 'anonymous') return 'Someone';
    if (effectiveAttr === 'first_name') return participantId.split('-')[0] || participantId;
    // initials
    const parts = participantId.split('-');
    const seed = parts[0] || participantId;
    return `${seed[0]?.toUpperCase() || 'X'}.`;
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="publication-editor-modal">
      <div className="bg-white rounded-lg p-5 max-w-5xl w-full space-y-4 max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-primary-900">Draft the shared story</h2>
          <Badge variant="outline">Attribution auto-picks the more private of the pair</Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-secondary-800">Draft (final wording)</div>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="min-h-[220px] mt-1"
                placeholder="Write the final story. Both people must approve this word for word before publish."
                data-testid="publication-final-text"
              />
            </div>
            <div className="space-y-2">
              {identifiable.map((p) => (
                <div key={p} className="border border-neutral-200 rounded-md p-3">
                  <div className="text-xs text-primary-900 font-mono mb-2">{p}</div>
                  <div className="grid gap-2 sm:grid-cols-3 items-center">
                    <label className="text-xs flex items-center gap-2">
                      Attribution
                      <select
                        value={attr[p] || 'initials'}
                        onChange={(e) => setAttr({ ...attr, [p]: e.target.value })}
                        className="border border-neutral-200 rounded-md px-2 py-1 text-sm"
                        data-testid={`publication-attr-${p}`}
                      >
                        <option value="initials">Initials</option>
                        <option value="first_name">First name</option>
                        <option value="anonymous">Anonymous</option>
                      </select>
                    </label>
                    <label className="text-xs flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!photoPerm[p]}
                        onChange={(e) => setPhotoPerm({ ...photoPerm, [p]: e.target.checked })}
                        data-testid={`publication-photo-${p}`}
                      />
                      Photo permission
                    </label>
                    <label className="text-xs flex items-center gap-2 font-semibold">
                      <input
                        type="checkbox"
                        checked={!!approvals[p]}
                        onChange={(e) => setApprovals({ ...approvals, [p]: e.target.checked })}
                        data-testid={`publication-approval-${p}`}
                      />
                      Approved final text
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-neutral-500">
              Server enforces: publishing requires approvals from every identifiable participant.
              Server also collapses attribution to the more private choice across the pair on publish.
            </div>
          </div>

          <div className="space-y-3" data-testid="publication-preview-panel">
            <div className="text-[10px] uppercase tracking-widest text-secondary-800">Reader preview</div>
            <div className="border border-neutral-200 rounded-md p-4 bg-neutral-50 min-h-[220px]">
              <div className="text-xs text-neutral-500 mb-2">
                Attribution effective: <span className="font-semibold" data-testid="preview-attribution-effective">{effectiveAttr}</span>
              </div>
              <div className="text-primary-900 whitespace-pre-wrap text-sm leading-relaxed" data-testid="preview-body">
                {text || 'Your draft appears here.'}
              </div>
              <div className="mt-3 text-xs text-neutral-600">
                {identifiable.map((p) => (
                  <div key={p} className="flex items-center gap-2">
                    <span className="font-medium">{readerName(p)}</span>
                    {photoPerm[p] ? (
                      <span className="text-emerald-700">portrait allowed</span>
                    ) : (
                      <span className="text-neutral-500">no portrait</span>
                    )}
                    {approvals[p] ? (
                      <span className="text-emerald-700">- approved</span>
                    ) : (
                      <span className="text-rose-600">- awaiting approval</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="text-[11px] text-neutral-500">
              This is what a reader will see once published. The story is not visible outside admin until every
              identifiable participant has approved.
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center gap-2">
          <div className="text-[11px] text-neutral-500" data-testid="publish-ready-indicator">
            {publishReady ? 'Publish is unlocked.' : 'Publish stays locked until both people approve the draft above.'}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onClose} data-testid="publication-cancel-btn">Cancel</Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="bg-primary-900 text-white hover:bg-primary-800"
              data-testid="publication-save-btn"
            >
              {saving ? 'Saving...' : 'Save draft'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RevokeDialog = ({ candidate, onClose }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 8) {
      toast.error('Please write a reason (at least 8 characters).');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/success-stories/${candidate.id}/revoke`, { reason: trimmed });
      toast.success('Revoked.');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="revoke-dialog">
      <div className="bg-white rounded-lg p-5 max-w-lg w-full space-y-3">
        <h3 className="font-display text-lg font-bold text-primary-900">Revoke publication</h3>
        <p className="text-sm text-neutral-600">
          Please note the reason for revocation. This is stored on the publication and audit-logged.
          Minimum 8 characters.
        </p>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Participant requested removal; new relationship context; content correction..."
          className="min-h-[100px]"
          data-testid="revoke-reason-textarea"
        />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="revoke-cancel-btn">Cancel</Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={saving}
            className="bg-rose-700 text-white hover:bg-rose-800"
            data-testid="revoke-confirm-btn"
          >
            {saving ? 'Revoking...' : 'Revoke publication'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSuccessStoriesPage;
